import { mergeDeepRight } from 'ramda';
import { readLocallyStored } from './local-storage';
import rwcInternalApi, { intApiExternalConfig } from './internal-api';
import type { RWCConfig } from './types';
import { isObject } from './misc';

export const mergeWithDevelopmentFlags = (config: RWCConfig): RWCConfig => {
    if (typeof window !== 'object') return config;

    const storageConfig = readLocallyStored('appConfig', {}, false) as RWCConfig;
    const configOverrides = readLocallyStored('appConfigOverrides', {}, true) as RWCConfig;

    return mergeDeepRight(config, mergeDeepRight(configOverrides, storageConfig)) as RWCConfig;
};

if (typeof window === 'object') {
    window.config = mergeWithDevelopmentFlags(window.config);
}

// small hack for tests
const CONFIG = typeof window === 'object' ? window.config : {};

/**
 * cachedConfig
 * original: Keep the last config and update it when it changes, Prevent the config from being updated
 * merged: Keeps the override config by brandId, reduction of merge
 */
const cachedConfig = { original: {}, merged: {} } as {
    original: RWCConfig;
    merged: RWCConfig;
    brandId?: string;
};

const brandIdSymbol = Symbol.for('rwcBrandId');
if (!globalThis[brandIdSymbol]) {
    globalThis[brandIdSymbol] = null;
}

export function setBrandIdForConfig(brandId) {
    globalThis[brandIdSymbol] = brandId;

    if (!('process' in globalThis)) {
        // eslint-disable-next-line no-console
        console.info('[vc:utils:config] config getConfig: brandId', brandId);
    }
}

const OVERRIDE_KEY = 'overrideConfigWithBrandIdMap';

function _getConfig<T>(config: T, brandId: string, result: any = {}): T {
    const overrides = config?.[OVERRIDE_KEY]?.[brandId] as any;

    for (const key in config) {
        if (key === OVERRIDE_KEY) continue;
        if (isObject(config[key])) {
            if (Array.isArray(config[key])) {
                result[key] = _getConfig(config[key], brandId, []);
            } else {
                result[key] = _getConfig(config[key], brandId);
            }
        } else {
            result[key] = config[key];
        }
    }

    if (isObject(overrides)) {
        result = mergeDeepRight(result, overrides);
    }

    return result;
}

function getConfig(): RWCConfig;
function getConfig<Key extends keyof RWCConfig>(key: Key): RWCConfig[Key];
/**
 * Config is loaded with sync request, because of browsers checks that should be passed right after load
 */
function getConfig<Key extends keyof RWCConfig>(key?: Key): RWCConfig | RWCConfig[Key] {
    const config = rwcInternalApi[intApiExternalConfig] ?? CONFIG;

    if (!config) throw new Error('Config is not loaded');

    if (
        !globalThis[brandIdSymbol] &&
        !('process' in globalThis) &&
        typeof window === 'object' &&
        window === window.top
    ) {
        // eslint-disable-next-line no-console
        console.warn(
            '[vc:utils:config] config getConfig: brandId should be initialized before using getConfig'
        );
    }

    if (cachedConfig.original !== config || cachedConfig.brandId !== globalThis[brandIdSymbol]) {
        const mergedConfig = _getConfig<RWCConfig>(config, globalThis[brandIdSymbol]);
        // reads the appConfig from the cache and performs a final merge.
        // priority: app.config -> brand override config -> sessionStorage appConfig
        cachedConfig.merged = mergeWithDevelopmentFlags(mergedConfig);
        cachedConfig.original = config;
        cachedConfig.brandId = globalThis[brandIdSymbol];
        if (!('process' in globalThis)) {
            // eslint-disable-next-line no-console
            console.info(
                '[vc:utils:config] config getConfig: config',
                cachedConfig.merged,
                globalThis[brandIdSymbol]
            );
        }
    }

    return key ? cachedConfig.merged[key] : cachedConfig.merged;
}

export default getConfig;
